// ─────────────────────────────────────────────────────────────────────────────
// Stripe Contractor Payouts
//
// IMPORTANT: Stripe payouts require a backend — you cannot call Stripe's
// transfer API from the browser (it requires your secret key).
//
// RECOMMENDED SETUP (when ready):
// 1. Create a Supabase Edge Function called "stripe-payout"
// 2. Add your STRIPE_SECRET_KEY to Supabase project secrets
// 3. The Edge Function creates a Stripe Transfer to the contractor's
//    connected account (stripe_account_id on the contractors table)
// 4. Replace SUPABASE_FUNCTION_URL below with your real Edge Function URL
//
// Until then, this service runs in DEMO MODE — simulates a payout with
// a 1.5 second delay and logs details to the console.
//
// STRIPE SETUP STEPS (one-time):
// 1. Go to https://dashboard.stripe.com → Activate your account
// 2. Enable Stripe Connect (for contractor payouts):
//    Dashboard → Connect → Get Started
// 3. Each contractor needs to onboard via a Stripe Connect link
//    (the "Invite to Stripe" button in your Contractors tab generates this)
// 4. After onboarding, their stripe_account_id is saved to your DB
// ─────────────────────────────────────────────────────────────────────────────

const STRIPE_PUBLIC_KEY         = 'YOUR_STRIPE_PUBLIC_KEY';   // pk_live_... or pk_test_...
const SUPABASE_FUNCTION_URL     = 'YOUR_SUPABASE_FUNCTION_URL'; // e.g. https://xxx.supabase.co/functions/v1/stripe-payout

export const IS_STRIPE_CONFIGURED = STRIPE_PUBLIC_KEY !== 'YOUR_STRIPE_PUBLIC_KEY';

/**
 * Pay a contractor for a completed job.
 * In demo mode: simulates success after 1.5s.
 * In production: calls Supabase Edge Function → Stripe Transfer API.
 */
export const payContractor = async ({ contractor, ticket, amount }) => {
  if (!IS_STRIPE_CONFIGURED) {
    console.log('💳 Stripe not configured — running in demo mode');
    console.log('💳 Would pay:', { contractor: contractor.name, amount, ticketId: ticket.id });
    await new Promise((r) => setTimeout(r, 1500));
    return {
      success: true,
      demo: true,
      stripePaymentId: `demo_${Date.now()}`,
      message: `Demo payout of $${amount} to ${contractor.name}`,
    };
  }

  try {
    const response = await fetch(SUPABASE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contractorStripeId: contractor.stripeAccountId,
        amountCents: Math.round(amount * 100),
        ticketId: ticket.id,
        description: `PropDesk: ${ticket.title} — Unit ${ticket.unit}`,
      }),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Payout failed');

    return { success: true, stripePaymentId: result.transferId };
  } catch (err) {
    console.error('Stripe payout error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Generate a Stripe Connect onboarding link for a contractor.
 * In demo mode: simulates the link.
 */
export const getContractorOnboardingLink = async (contractorId, contractorEmail) => {
  if (!IS_STRIPE_CONFIGURED) {
    console.log('💳 Demo mode — would open Stripe Connect onboarding for:', contractorEmail);
    return { success: true, demo: true, url: 'https://connect.stripe.com/demo' };
  }

  try {
    const response = await fetch(`${SUPABASE_FUNCTION_URL}/onboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contractorId, email: contractorEmail }),
    });
    const result = await response.json();
    return { success: true, url: result.url };
  } catch (err) {
    return { success: false, error: err.message };
  }
};
